import {GoogleMap, Marker, withGoogleMap, withScriptjs} from 'react-google-maps';
import React, {useEffect, useState} from 'react';
import {IMapProps, MapPosition} from '../../types';

type Props = IMapProps;

const FALLBACK_POSITION: MapPosition = {lat: 0, lng: 0};
const FALLBACK_ZOOM = 1;
const ADDRESS_ZOOM = 11;

// Geocoding results are shared between map instances, so each address is requested only once per page load.
const geocodeCache: {[address: string]: Promise<MapPosition | null>} = {};

const geocodeAddress = (address: string): Promise<MapPosition | null> => {
    const cached = geocodeCache[address];
    if (cached) {
        return cached;
    }

    const request = new Promise<MapPosition | null>((resolve) => {
        const geocoder = new (window as any).google.maps.Geocoder();
        geocoder.geocode({address}, (results: any[] | null, status: string) => {
            if ('OK' !== status || !results || !results.length) {
                console.warn(`Map: geocoding "${address}" failed with status ${status}`);
                resolve(null);
                return;
            }
            const location = results[0].geometry.location;
            resolve({lat: location.lat(), lng: location.lng()});
        });
    });
    request.then((position) => {
        // Do not keep failures cached, so a transient error can be retried on the next view.
        if (null === position) {
            delete geocodeCache[address];
        }
    });
    geocodeCache[address] = request;

    return request;
};

const MapComponent = withScriptjs(
    withGoogleMap((props: Props) => {
        const [position, setPosition] = useState<MapPosition | null>(null);
        const [isResolved, setIsResolved] = useState(false);

        useEffect(() => {
            let isActive = true;
            setIsResolved(false);
            const address = props.address.trim();
            if (!address) {
                setPosition(null);
                setIsResolved(true);
                return;
            }

            geocodeAddress(address).then((result) => {
                if (isActive) {
                    setPosition(result);
                    setIsResolved(true);
                }
            });

            return () => {
                isActive = false;
            };
        }, [props.address]);

        if (!isResolved) {
            return null;
        }

        const marker =
            'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABcAAAAeCAMAAADw+3VaAAABIFBMVEUAAAD/AAD/gID/VVX/VVXbSUnfYGDjVVXoXV3qVVXvUFDxVVXmWVnnVVXoUVHpWVnrWFjsVVXuVVXqVVXrV1fpU1PrUlfsVVXpVVXpUlbrVFTpVFTqVVXqVFTrVVXrVFTrVVXoVFTqVVXrVVXpVVXqU1bpVFTqU1brVVXpVVXpVFTqVFbrVFbpVFbqVFTrU1XqU1XqVFbqVFTrU1XpU1XpVFTqU1XrU1XpVFbqVFbqU1XqVFTqVFXpVFXqVFXqVFXqVFXrVFXqVVXqVFXqVVXrVFXqVFXqVFXqVFXrVFXpVFXqVFXqVFXqVFXqVFXqVFXqVFXqVFXqVFXqVFXqVFXqVFXqVFXqVFXqVFXqVFXpVFXqVFXqVFXqVFXqVFXqVFX///+wwJ9YAAAAXnRSTlMAAQIDBgcICQsMEBIUFRYXGhseJCYuMjY5O0BGSElLTE5PV1pdYmprcnV2en2AiIqQkpSWmZqfoqSnqKmtsbm/xcjK0dPU19jd4OHk6Onr7O7v8PHy8/X3+Pn6+/3+S6QWrgAAAAFiS0dEX3PRUS0AAAEQSURBVBgZZcGJIgJhGAXQmxqVECohIZSylTVaqLEmyhKi5r7/Y/j+aaZpcg4GtNT5bavbuN6NYIRn+4mWfnEBtkCFIzrrGAjodOltQvGUOOY7DrFFxSgnprVotknlzgv4Hik6SZiCVSppYI3CSMLiq1HowBlFGUOxPkljDjcUCTjqFCm0KEJwHFNk8EsxCcchxR5aFFE4ShQZ6BRZDGltilWcUDSDsOUojFlsUKn6MLDSpdAB/xuVWgxCy3Wp5AAUaOrXj/KXbZq6MwDmvziuACXPMZ9hKKFXuu1jIE2Xez8sFY7oxWELv9BxAMfyD21XXozYoeVhCi4Fmp4jcJu4oPhYxDhvkXxfwn/e00YcQ399jYClMs+xVwAAAABJRU5ErkJggg==';

        return (
            <GoogleMap
                zoom={position ? ADDRESS_ZOOM : FALLBACK_ZOOM}
                center={position || FALLBACK_POSITION}
                defaultOptions={{
                    // Static view: no controls, no panning/zooming, no clickable POIs.
                    disableDefaultUI: true,
                    gestureHandling: 'none',
                    keyboardShortcuts: false,
                    clickableIcons: false,
                }}>
                {props.isMarkerShown && position && <Marker position={position} icon={{url: marker}} clickable={false} />}
            </GoogleMap>
        );
    })
);

export default MapComponent;
